import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrg } from '@/stores/org'
import { useToast } from '@/components/ui/toast'
import { bindReferral } from '@/lib/resources/acquisition'
import {
  beginReferralBind,
  clearReferralCode,
  hasBoundReferral,
  markReferralBound,
  pendingReferralCode,
  readAcquisitionEntry,
  referralNotice,
  type AcquisitionEntry,
} from '@/lib/acquisition'

/**
 * Binds the org's pending referral code once, on the org's first
 * authenticated interaction after an invite link landed (ADR-0042 decision
 * 3 — the invite code is the inviter's org id). Renders nothing and blocks
 * nothing: it fires only when a `?ref=` code is pending, exactly once per
 * org per browser, and is fully silent on transport failure — signup
 * success never depends on it, and a refusal is a 200 decision that maps
 * to a calm notice, never an error.
 *
 * An org with no pending code does NOT consume the latch: a user who signs
 * up unattributed and later clicks an invite link should still bind. The
 * attempt is deliberately NOT cancelled on unmount: it owns no component
 * state, its completion is idempotent (one localStorage marker + one
 * toast), and a StrictMode double-mount would otherwise cancel the only
 * attempt the in-memory latch allows — losing the notice and the marker.
 */
export function ReferralBind() {
  const { t } = useTranslation()
  const { activeOrgId } = useOrg()
  const { toast } = useToast()

  useEffect(() => {
    if (!activeOrgId) return
    if (hasBoundReferral(activeOrgId)) return
    const code = pendingReferralCode()
    if (!code) return
    if (!beginReferralBind(activeOrgId)) return
    const entry: AcquisitionEntry = readAcquisitionEntry() ?? { channel: 'unknown', source: null }

    void (async () => {
      try {
        const decision = await bindReferral(activeOrgId, { code, channel: entry.channel, source: entry.source })
        // Any definitive answer closes the org — a retry or refresh stays silent.
        markReferralBound(activeOrgId)
        clearReferralCode()
        const notice = referralNotice(decision.reason)
        if (!notice) return
        toast({
          variant: notice.variant,
          title: t(notice.titleKey),
          description: notice.descriptionKey ? t(notice.descriptionKey) : undefined,
        })
      } catch {
        // Transport failure: stay silent (no dialog, no error screen) and leave
        // the org unbound so a later visit can retry with the same code.
      }
    })()
  }, [activeOrgId, toast, t])

  return null
}
